<template>
	<aside class="change-plan-sidebar bg-shade-2 p-5 pt-4 border-t b7:border-l b7:border-t-0 border-subtle">
		<h2 class="font-display text-4 mt-5 mb-4">Subscription Details</h2>

		<div v-if="step === 'checkout' && showIntervalToggle" class="flex justify-center mb-4">
			<CBillingModeToggle
				compact
				:value="interval"
				:savings-percent="annualSavingsPercent"
				@change="$emit('update:interval', $event)"
			/>
		</div>
		<CPlanSummaryCard :plan="plan" :is-annual="isAnnual" :annual-monthly-price="annualMonthlyPrice" />
		<CCallout v-if="showThrottled" type="error" class="mb-4">
			We are unable to process your request at the moment. Please try again later.
		</CCallout>
		<CCallout v-else-if="isScheduledDowngrade" type="info" class="mb-4">
			{{ scheduledDowngradeMessage }}
		</CCallout>
		<div v-else-if="showLoadingIndicator" class="flex justify-center py-5 mb-4">
			<Inline svg="loading" class="spin" />
		</div>
		<div v-else class="change-plan-sidebar__table-container">
			<table class="w-full mb-4" :class="{ 'opacity-50': costLoading }">
				<tbody>
					<tr v-for="(item, index) in displayedLineItems" :key="`${item.label}-${index}`" class="border-b">
						<th class="py-3 pr-3 text-left w-full" :class="item.emphasized ? 'text-3 font-display' : 'font-normal'">
							{{ item.label }}
							<CToolTip v-if="item.customizable && step === 'checkout' && showCustomize" placement="top" :visible-arrow="false" is-interactive>
								<template #button>
									<Inline svg="info" class="align--no-descender align hover:text-accent-7" />
								</template>
								Suggested Usage Wallet amounts are based on your last 12 mo. of usage.
								Your selected wallet amount reflects any customizations you made.
								<button class="button button--no-style text-link underline" @click="$emit('customize')">Customize your usage wallet.</button>
							</CToolTip>
						</th>
						<td class="py-3 text-right whitespace-nowrap">{{ formatCurrencyAccounting(item.amount) }}</td>
					</tr>
				</tbody>
				<tfoot>
					<template v-if="serverInvoice && !showEstimates">
						<tr>
							<th class="py-3 pr-3 text-right w-full">Subtotal:</th>
							<td class="py-3 text-right whitespace-nowrap">{{ formatCurrencyAccounting(serverInvoice.Subtotal) }}</td>
						</tr>
						<tr v-if="serverInvoice.BalanceApplied < 0">
							<th class="py-3 pr-3 text-right w-full">Credits Applied:</th>
							<td class="py-3 text-right whitespace-nowrap">{{ formatCurrencyAccounting(serverInvoice.BalanceApplied) }}</td>
						</tr>
						<tr v-if="serverInvoice.DiscountPercent > 0">
							<th class="py-3 pr-3 text-right w-full font-normal">{{ serverInvoice.DiscountDescription }}:</th>
							<td class="py-3 text-right whitespace-nowrap">{{ formatCurrencyAccounting(-serverInvoice.DiscountAmount) }}</td>
						</tr>
						<tr v-if="serverInvoice.TaxAmount !== 0 || serverInvoice.TaxDisplayText">
							<th class="py-3 pr-3 text-right w-full font-normal">{{ serverInvoice.TaxDisplayText || 'Sales Tax' }}:</th>
							<td class="py-3 text-right whitespace-nowrap">{{ formatCurrencyAccounting(serverInvoice.TaxAmount) }}</td>
						</tr>
						<tr v-if="(costModel?.unpaidInvoiceAmount ?? 0) > 0">
							<th class="py-3 pr-3 text-right w-full font-normal">Outstanding Balance:</th>
							<td class="py-3 text-right whitespace-nowrap">{{ formatCurrencyAccounting(costModel?.unpaidInvoiceAmount ?? 0) }}</td>
						</tr>
						<tr>
							<th class="py-3 pr-3 text-right w-full text-3">{{ amountDue < 0 ? 'Credit Amount:' : 'Amount Due:' }}</th>
							<td class="py-3 text-right"><strong>{{ formatCurrencyAccounting(amountDue) }}</strong></td>
						</tr>
					</template>
					<tr v-else>
						<th class="py-3 pr-3 text-right w-full">Estimated Subtotal:</th>
						<td class="py-3 text-right whitespace-nowrap">{{ formatCurrencyAccounting(subtotal) }}</td>
					</tr>
				</tfoot>
			</table>
		</div>
		<div class="flex justify-end gap-4">
			<button v-if="step === 'customize'" class="button button--primary" @click="$emit('next')">Next</button>
			<CToolTip v-if="step === 'checkout'" :disabled="!costPending" placement="top" :visible-arrow="false" class="w-full">
				<template #button>
					<StatusButton
						class="button--primary w-full"
						:status="costPending || costThrottled || (!serverInvoice && !isScheduledDowngrade) ? 'disabled' : submitStatus"
						@click="$emit('submit')"
						@reset-button-status="$emit('reset-status')"
					>
						{{ submitButtonLabel }}
					</StatusButton>
				</template>
				Calculating tax...
			</CToolTip>
		</div>
		<p v-if="step === 'checkout'" class="text-2 text-subtle mt-3">Your plan will automatically renew every {{ isAnnual ? 'year' : 'month' }} until you cancel your subscription.</p>
		<div v-if="step === 'checkout' && isAnnual" class="flex justify-center">
			<button
				type="button"
				class="button--no-style font-display mt-5 b7:!mt-6 flex items-center gap-4 text-left text-5"
				@click="showGuaranteeDialog = true"
			>
				<Inline svg="images/money-badge-sm" class="w-6 shrink-0" />
				<span class="sr-only">30-Day</span>
				Money-Back<br>Guarantee
			</button>
		</div>
		<CMoneyBackGuaranteeDialog
			v-if="isAnnual"
			:visible="showGuaranteeDialog"
			@closed="showGuaranteeDialog = false"
		/>
	</aside>
</template>

<script lang="ts">
	import { defineComponent, computed, ref, type PropType } from 'vue';
	import type { Plan } from '@cognitoforms/types/server-types/plans/model/plan';
	import { LineItemType } from '@cognitoforms/types/server-types/plans/model/line-item-type';
	import type { SubscriptionCostViewModel } from '@cognitoforms/api/services/subscription-service';
	import CBillingModeToggle from 'src/components/pricing/BillingModeToggle.vue';
	import CPlanSummaryCard from 'src/components/pricing/PlanSummaryCard.vue';
	import CToolTip from 'src/components/interaction/ToolTip.vue';
	import CCallout from 'src/components/content/Callout.vue';
	import type { ButtonStatus } from 'src/components/interaction/StatusButton.vue';
	import StatusButton from 'src/components/interaction/StatusButton.vue';
	import type { BillingMode } from 'src/components/pricing/billing-mode';
	import { formatCurrencyAccounting } from 'src/utilities/currency';
	import { getPlanNameWithCadence } from 'src/components/pricing/plan-name-with-cadence';
	import CMoneyBackGuaranteeDialog from 'src/components/dialogs/MoneyBackGuaranteeDialog.vue';

	interface LineItem {
		label: string;
		amount: number;
		emphasized?: boolean;
		customizable?: boolean;
		discount?: boolean;
		type?: LineItemType;
	}

	export default defineComponent({
		components: {
			CBillingModeToggle,
			CPlanSummaryCard,
			CToolTip,
			CCallout,
			StatusButton,
			CMoneyBackGuaranteeDialog
		},
		props: {
			plan: {
				type: Object as PropType<Plan>,
				required: true
			},
			currentPlanName: {
				type: String,
				default: null
			},
			currentPlanIsAnnual: {
				type: Boolean,
				default: false
			},
			annualMonthlyPrice: {
				type: Number,
				required: true
			},
			annualSavingsPercent: {
				type: Number,
				default: 0
			},
			interval: {
				type: String as PropType<BillingMode>,
				default: 'annual'
			},
			step: {
				type: String as PropType<'customize' | 'checkout'>,
				default: 'customize'
			},
			showCustomize: {
				type: Boolean,
				default: false
			},
			showIntervalToggle: {
				type: Boolean,
				default: true
			},
			users: { type: Number, required: true },
			userTotal: { type: Number, required: true },
			costModel: {
				type: Object as PropType<SubscriptionCostViewModel | null>,
				default: null
			},
			costLoading: {
				type: Boolean,
				default: false
			},
			costThrottled: {
				type: Boolean,
				default: false
			},
			costStale: {
				type: Boolean,
				default: false
			},
			submitStatus: {
				type: String as PropType<ButtonStatus>,
				default: 'default'
			}
		},
		emits: ['next', 'submit', 'update:interval', 'customize', 'reset-status'],
		setup(props, { emit }) {
			const isAnnual = computed(() => props.interval === 'annual');
			const hasAnnualDiscount = computed(() => props.plan.MonthlyAmount > 0 && props.annualMonthlyPrice < props.plan.MonthlyAmount);
			const additionalUsers = computed(() => Math.max(props.users - props.plan.UserLimit, 0));
			const committedUsers = computed(() => isAnnual.value ? (props.costModel?.committedUsers ?? 0) : 0);
			const netAdditionalUsers = computed(() => additionalUsers.value - committedUsers.value);
			const additionalUsersLabel = computed(() => {
				if (!isAnnual.value)
					return `Additional Users (${additionalUsers.value} at $${props.plan.AdditionalUserFee})`;
				else if (netAdditionalUsers.value < 0)
					return `Additional Users - ${-netAdditionalUsers.value} for 12 months (now included in ${props.plan.DisplayName})`;
				else
					return `Additional Users - ${netAdditionalUsers.value} for 12 months`;
			});
			const serverInvoice = computed(() => props.costModel?.invoice ?? null);
			const serverLineItems = computed<LineItem[]>(() => {
				const items: LineItem[] = [];
				let annualDiscount = 0;
				let discountIndex = -1;
				for (const item of serverInvoice.value?.LineItems ?? []) {
					const lineItem: LineItem = {
						label: item.Description,
						amount: item.Amount,
						type: item.Type,
						customizable: item.Type === LineItemType.UsageWalletFunding
					};
					// A mid-term upgrade splits the commitment across funding lines, so aggregate the savings into a single discount row.
					if (item.Type === LineItemType.AnnualPlanCommitFunding && hasAnnualDiscount.value && props.annualMonthlyPrice > 0) {
						const fullPrice = item.Amount * props.plan.MonthlyAmount / props.annualMonthlyPrice;
						annualDiscount += item.Amount - fullPrice;
						items.push({ ...lineItem, amount: fullPrice });
						discountIndex = items.length;
					}
					else
						items.push(lineItem);
				}
				if (annualDiscount !== 0)
					items.splice(discountIndex, 0, { label: 'Annual Plan Discount', amount: annualDiscount, discount: true });
				return items;
			});
			const costPending = computed(() => props.costLoading || props.costStale);
			const showEstimates = computed(() => props.step === 'customize' || costPending.value);
			const displayedLineItems = computed<LineItem[]>(() => {
				if (!serverInvoice.value)
					return [];
				if (!showEstimates.value)
					return serverLineItems.value;

				const items = [...serverLineItems.value];
				const estimate = (type: LineItemType, label: string, amount: number) => {
					const index = items.findIndex(item => item.type === type);
					if (index >= 0 && amount === 0)
						items.splice(index, 1);
					else if (index >= 0)
						items.splice(index, 1, { ...items[index], label, amount });
					else if (amount !== 0)
						items.push({ label, amount, type });
				};
				estimate(LineItemType.AnnualUserCommitFunding, additionalUsersLabel.value, props.userTotal);
				// Only relabel the wallet line: the server amount is the shortfall actually charged, not the slider target.
				const walletIndex = items.findIndex(item => item.type === LineItemType.UsageWalletFunding);
				if (walletIndex >= 0)
					items.splice(walletIndex, 1, { ...items[walletIndex], label: 'Usage Wallet for Add-ons' });
				return items;
			});
			const subtotal = computed(() => displayedLineItems.value.reduce((sum, item) => sum + item.amount, 0));
			const amountDue = computed(() => serverInvoice.value ? serverInvoice.value.AmountDue + (props.costModel?.unpaidInvoiceAmount ?? 0) : 0);
			const showThrottled = computed(() => props.step === 'checkout' && props.costThrottled);
			const isScheduledDowngrade = computed(() => {
				if (props.step !== 'checkout' || !props.costModel || costPending.value || props.costThrottled)
					return false;
				if (props.costModel.isScheduledDowngrade === true)
					return true;
				return !serverInvoice.value;
			});
			const submitButtonLabel = computed(() => {
				if (!isScheduledDowngrade.value)
					return 'Pay Now';
				return props.costModel?.isScheduledTierDowngrade ? 'Schedule Downgrade' : 'Schedule Change';
			});
			const scheduledDowngradeRenewalDate = computed(() => {
				if (!props.costModel?.scheduledDowngradeRenewalDate)
					return null;
				const renewalDate = new Date(props.costModel.scheduledDowngradeRenewalDate);
				if (Number.isNaN(renewalDate.getTime()))
					return null;
				return renewalDate.toLocaleDateString('en-US', { timeZone: 'UTC' });
			});
			const currentPlanDisplay = computed(() => props.currentPlanName ? getPlanNameWithCadence(props.currentPlanName, props.currentPlanIsAnnual) : 'your current plan');
			const newPlanDisplay = computed(() => {
				const name = props.plan.DisplayName || props.plan.Name;
				if (!name)
					return 'your new plan';
				return getPlanNameWithCadence(name, isAnnual.value);
			});
			const scheduledDowngradeMessage = computed(() =>
				`You will remain on ${currentPlanDisplay.value} until ${scheduledDowngradeRenewalDate.value ?? 'your renewal date'}, and then your organization will be moved to ${newPlanDisplay.value}.`);
			const showLoadingIndicator = computed(() => !serverInvoice.value && !isScheduledDowngrade.value);
			const showGuaranteeDialog = ref(false);
			return {
				formatCurrencyAccounting,
				isAnnual,
				subtotal,
				serverInvoice,
				costPending,
				showEstimates,
				displayedLineItems,
				amountDue,
				showThrottled,
				isScheduledDowngrade,
				submitButtonLabel,
				scheduledDowngradeMessage,
				showLoadingIndicator,
				showGuaranteeDialog
			};
		}
	});
</script>
<style lang="scss">
.change-plan-sidebar {
	@include b-up($b7) {
		width: var(--checkout-flow-sidebar-width);
	}

	&__table-container {
		min-height: 200px;
	}
}
</style>